/*
 * Copyright (c) 2022-2022. Revo Digital
 * ---
 * Author: gabriele
 * File: DataOriginDialog.tsx
 * Project: mle-client-n
 * Committed last: 2022/5/6 @ 1644
 * ---
 * Description:
 */

import { observer }            from 'mobx-react';
import classNames              from 'classnames';
import {
  currentThemeIsDark
}                              from '../../../../common/utils';
import {
  Callout,
  Classes,
  Dialog,
  Intent,
  NonIdealState
}                              from '@blueprintjs/core';
import { useSelector }         from 'react-redux';
import {
  RootState
}                              from '../../../../store';
import { useEffect, useState } from 'react';
import {
  DataOrigin
}                              from '@revodigital/mle-dataresolver/lib/entities';
import {
  DataOriginItem
}                              from '../../../dataorigins/DataOriginItem';
import {
  mleDomainGqlClient
}                              from '../../../../api/domain';
import {
  GET_DATAORIGINS_BY_COMPANY_ID
}                              from '../../../../api/domain/queries.gql';
import {
  REMOVE_DATAORIGIN
}                              from '../../../../api/domain/mutations.gql';
import {
  MleToaster,
  Toasts
}                              from '../../../../common/toast';
import {
  BaseDialogProps,
  HasEditor
}                              from '../../../../common/props';
import {
  MyLabelLoginDialog
}                              from '../MyLabelLoginDialog';
import {
  MyLabelAuthFeature
}                              from '../../../../store/features/mylabel/auth/MyLabelAuthFeature';

interface Props extends HasEditor, BaseDialogProps {
  onLinkDataOrigin: () => void;
}

export const DataOriginDialog = observer((props: Props) => {
  const authData = useSelector((state: RootState) => state.authState);
  const [ mlLoginVisible, setMlLoginVisible ] = useState(false);
  const [ dataOrigins, setDataOrigins ] = useState<DataOrigin[]>([]);
  const [ isLoading, setIsLoading ] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const companyId = authData.user?.company?.id;
        const result = await mleDomainGqlClient.query({
          query: GET_DATAORIGINS_BY_COMPANY_ID,
          variables: {
            id: companyId
          },
          fetchPolicy: 'network-only'
        });

        const connstate = await MyLabelAuthFeature.checkConnection(companyId as number);
        props.editor.mylabelAuthFeature.setIsConnected(connstate);

        setDataOrigins(result.data.company.dataOrigins);
      } catch (e) {}
    })();
  }, [ dataOrigins, setDataOrigins, isLoading ]);

  const handleDataOriginDeletion = async (id: number) => {
    setIsLoading(true);
    await mleDomainGqlClient.mutate({
      mutation: REMOVE_DATAORIGIN,
      variables: { id }
    });
    setIsLoading(false);
    MleToaster.getInstance().show(Toasts.DATA_ORIGIN_REMOVED);
  };

  const handleDataOriginRefresh = async (id: number) => {
    setIsLoading(true);

    // Perform auth or require new credentials
    const res = await props.editor.mylabelAuthFeature.performAuthOnService();

    setIsLoading(false);

    if (!res) {
      setMlLoginVisible(true);
    } else MleToaster.getInstance().show(Toasts.ML_LINK_SUCCESSFUL);

  };

  return (
    <Dialog
      usePortal
      className={ classNames({ 'bp4-dark': currentThemeIsDark() }) }
      style={ { width: '50rem', height: '30rem' } }
      isOpen={ props.isOpen }
      title="Libreria origini dati collegate"
      onClose={ props.onClose }
      canOutsideClickClose={ false }
    >
      <div className={ Classes.DIALOG_BODY }>
        <Callout
          intent={ Intent.PRIMARY }
          className="mb-3"
        >
          La libreria è condivisa tra tutti i progetti e i membri
          di <strong>{ authData.user?.company?.name }</strong>
        </Callout>
        <div className="h-3/4">
          {
            dataOrigins.length > 0 ?
            dataOrigins.map(it => <DataOriginItem
              userName={ localStorage.getItem(MyLabelAuthFeature.__usr_key)! }
              disabled={ isLoading }
              editor={ props.editor }
              dataOrigin={ it }
              onDelete={ handleDataOriginDeletion }
              onRefresh={ handleDataOriginRefresh }
            />) :
            <div className="h-3/4 grid place-content-center">
              <NonIdealState icon="data-connection"
                             title="Nessuna origine dati collegata"
                             description="Associa una nuova origine dati per iniziare ad utilizzarle"/>
            </div>
          }
        </div>
      </div>
      {
        mlLoginVisible &&
        <MyLabelLoginDialog onLoginSuccess={ () => setMlLoginVisible(false) }
                            editor={ props.editor } isOpen={ mlLoginVisible }
                            onClose={ () => setMlLoginVisible(false) }/>
      }
    </Dialog>
  );
});
